import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, ArrowRight } from 'lucide-react';

// V16.2 — natural, rotating goal phrasing (changes with the pregnancy week
// so it never feels repetitive; only shown when the learner set goals).
const GOAL_PHRASES = {
  gain_strength: 'gently building your strength',
  mentally_calm: 'keeping your mind calm',
  more_active: 'keeping you moving',
  normal_delivery: 'preparing your body for birth',
  better_sleep: 'helping you rest better',
  pain_relief: 'easing those aches',
};

export const CoachCard = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get('/api/prenatal-coach/status').then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  if (!status) return null;
  if (status.stage === 'postnatal') return null;
  const hasAccess = status.access_enabled;

  const goals = status.onboarding?.goals || [];
  const goalPhrase = goals.length > 0
    ? GOAL_PHRASES[goals[(status.week || 0) % goals.length]]
    : null;
  const readyLine = goalPhrase
    ? `Your week ${status.week || ''} practice plan is ready — ${goalPhrase}, one class at a time.`
    : `Your week ${status.week || ''} practice plan is ready and waiting for you.`;

  return (
    <Card className="rounded-2xl border-primary/20 bg-gradient-to-br from-primary/5 via-rose-50/50 to-transparent" data-testid="coach-dashboard-card">
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-heading text-base font-semibold">SoulAI Prenatal Coach</p>
                {!hasAccess && <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]" data-testid="coach-coming-soon-badge">Coming Soon</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md" data-testid="coach-card-subtitle">
                {hasAccess
                  ? (status.onboarded
                      ? readyLine
                      : 'Answer a few quick questions and SoulAI will build your personalised weekly practice.')
                  : 'Personalised weekly video classes, safely matched to your pregnancy — launching soon.'}
              </p>
            </div>
          </div>
          {hasAccess && (
            <Button size="sm" onClick={() => navigate('/learner/soulai-coach')} data-testid="coach-open-btn">
              {status.onboarded ? 'Open my plan' : 'Get started'}<ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
